/* eslint-disable @typescript-eslint/naming-convention */
import { GridsterItem } from "angular-gridster2";
import { ModuleManagerDateRangeInfo, ModuleManagerModuleSettings } from "src/shared/models/module-manager/module-manager-settings.models";

export class ModuleInfo {
    DTO: DashboardModuleResponsiveInstance;
    ModuleID: number;
    ModuleName: string;
    ModuleSource: string;
    PreviewImagePath: string;
    Resizable: boolean;
    CustomName?: string;
    LocalizedName: string;
    LocalizedGroup: string;
    ModuleDescription: string;
    ModuleGroup: string;
    RelatedID?: number;
    cxonePosition?: number;

    moduleKey?: string;
    map: DashboardModuleResponsiveInstanceMap;
    fromTaskbar?: boolean;

    options: {
        canExportAndSubscribe: boolean;
        chartAvailable: boolean;
        fullScreenViewAvailable: boolean;
        hasGenericSettings: boolean;
        mobileViewAvailable: boolean;
        settingsAvailable: boolean;
        tableAndChartAvailable: boolean;
        tableAvailable: boolean;
    };

    static fromModuleInstance(moduleApiModel: DashboardModuleResponsiveInstance) {
        return {
            DTO: moduleApiModel,
            ModuleID: moduleApiModel.ModuleID,
            ModuleName: moduleApiModel.ModuleName,
            ModuleDescription: moduleApiModel.ModuleDescription,
            ModuleGroup: moduleApiModel.ModuleGroup,
            Resizable: moduleApiModel.Resizable,
            PreviewImagePath: moduleApiModel.PreviewImagePath,
            RelatedID: moduleApiModel.RelatedID,
            map: moduleApiModel.map,
            options: {
                tableAvailable: moduleApiModel.TableAvailable,
                chartAvailable: moduleApiModel.ChartAvailable,
                tableAndChartAvailable: moduleApiModel.TableAndChartAvailable,
                mobileViewAvailable: moduleApiModel.MobileViewAvailable,
                fullScreenViewAvailable: moduleApiModel.FullScreenViewAvailable,
                settingsAvailable: moduleApiModel.SettingsAvailable,
                canExportAndSubscribe: moduleApiModel.CanExportAndSubscribe,
                hasGenericSettings: moduleApiModel.SettingEmpSelector
                    || moduleApiModel.SettingRecSelector
                    || moduleApiModel.SettingAttSelector
                    || moduleApiModel.SettingMetricSelector
                    || moduleApiModel.SettingDateRangeSelector
                    || moduleApiModel.SettingTimeRangeSelector
                    || moduleApiModel.SettingIntervalSelector
            },
            ModuleSource: moduleApiModel.ModuleSource
        } as ModuleInfo;
    }
}

export interface DashboardModuleResponsiveDTO {
    ModuleID: number;
    CompanyID: number;
    ModuleName: string;
    ModuleDescription: string;
    ModuleGroup: string;
    ModuleSource: string;
    IconPath: string;
    PreviewImagePath: string;
    Resizable: boolean;
    DefaultSizeX: number;
    DefaultSizeY: number;
    MinSizeX?: number;
    MinSizeY?: number;
    MaxSizeX?: number;
    MaxSizeY?: number;
    TableAvailable: boolean;
    ChartAvailable: boolean;
    TableAndChartAvailable: boolean;
    MobileViewAvailable: boolean;
    FullScreenViewAvailable: boolean;
    SettingsAvailable: boolean;
    CanExportAndSubscribe: boolean;
    SettingEmpSelector: boolean;
    SettingRecSelector: boolean;
    SettingAttSelector: boolean;
    SettingMetricSelector: boolean;
    SettingDateRangeSelector: boolean;
    SettingTimeRangeSelector: boolean;
    SettingIntervalSelector: boolean;
    MaxMetricCount: number;
    GroupSelectorShowToggle: boolean;
    ReadAccess: boolean;
    GroupSelectorHasMulti: boolean;
    DateRangeSelectorHasDOW: boolean;
    SystemAdminAccess: boolean;
    SettingSoloUserAttSelector: boolean;
    IncludeUserAttMetrics: boolean;
    SettingRemovePocFilter: boolean;
    SettingViewByLimitsFilters?: number;
}

export interface DashboardModuleResponsive extends DashboardModuleResponsiveDTO {
    LocalizedName: string;
    LocalizedGroup: string;
    RelatedID: number;
}

export interface DashboardModuleEagerLoad {
    moduleState: Record<string, any>;
    dateSelectorHtml: string;
    controlSettings: ModuleManagerModuleSettings;
    availableDateRanges: ModuleManagerDateRangeInfo[];
}

export interface DashboardModuleResponsiveInstanceMap {
    size: number[];
    position?: number[];
    maxSize?: number[];
    minSize?: number[];
}

export interface DashboardModuleResponsiveInstance extends DashboardModuleResponsiveDTO {
    SettingEmpLevels?: number[];
    SettingRecLevels?: number[];
    moduleKey?: string;
    name?: string;
    map?: DashboardModuleResponsiveInstanceMap;
    hasAccess?: boolean;
    loginID?: number;
    dashboardKey?: string;
    RelatedID?: number;
    isPlaceholder?: boolean;
    CustomName?: string;
    fromTaskbar?: boolean;
}

export class DashboardModuleResponsiveInstanceForGridster implements DashboardModuleResponsiveInstance, GridsterItem {
    SettingEmpLevels?: number[];
    SettingRecLevels?: number[];
    moduleKey?: string;
    name?: string;
    hasAccess?: boolean;
    loginID?: number;
    dashboardKey?: string;
    RelatedID?: number;
    isPlaceholder?: boolean;
    fromTaskbar?: boolean;
    ModuleID: number;
    CompanyID: number;
    ModuleName: string;
    ModuleDescription: string;
    ModuleGroup: string;
    ModuleSource: string;
    IconPath: string;
    PreviewImagePath: string;
    Resizable: boolean;
    DefaultSizeX: number;
    DefaultSizeY: number;
    MinSizeX?: number;
    MinSizeY?: number;
    MaxSizeX?: number;
    MaxSizeY?: number;
    TableAvailable: boolean;
    ChartAvailable: boolean;
    TableAndChartAvailable: boolean;
    MobileViewAvailable: boolean;
    FullScreenViewAvailable: boolean;
    SettingsAvailable: boolean;
    CanExportAndSubscribe: boolean;
    SettingEmpSelector: boolean;
    SettingRecSelector: boolean;
    SettingAttSelector: boolean;
    SettingMetricSelector: boolean;
    SettingDateRangeSelector: boolean;
    SettingTimeRangeSelector: boolean;
    SettingIntervalSelector: boolean;
    MaxMetricCount: number;
    GroupSelectorShowToggle: boolean;
    ReadAccess: boolean;
    GroupSelectorHasMulti: boolean;
    DateRangeSelectorHasDOW: boolean;
    SystemAdminAccess: boolean;
    SettingSoloUserAttSelector: boolean;
    IncludeUserAttMetrics: boolean;
    SettingRemovePocFilter: boolean;
    SettingViewByLimitsFilters?: number;

    x: number;
    y: number;
    rows: number;
    cols: number;
    maxItemRows?: number;
    minItemRows?: number;
    maxItemCols?: number;
    minItemCols?: number;

    static fromDashboardModuleResponsiveInstance(instance: DashboardModuleResponsiveInstance) {
        const forGridster = new DashboardModuleResponsiveInstanceForGridster();
        Object.assign(forGridster, instance);
        Object.assign(forGridster, {
            x: instance.map.position[1],
            y: instance.map.position[0],
            rows: instance.map.size[1],
            cols: instance.map.size[0],
            maxItemRows: instance.map.maxSize[1],
            maxItemCols: instance.map.maxSize[0],
            minItemRows: instance.map.minSize[1] ?? 2, // Always force minSizeY of 2 if it is NULL in DB
            minItemCols: instance.map.minSize[0] ?? 4, // Always force minSyzeX of 4 if it is NULL in DB
        });

        return forGridster;
    }


}

export interface DashboardModuleInstance {
    DashboardKey: string;
    Instance: CopyModule;
}

export type DashboardModuleMap = {
    moduleKey: string;
    position: number[];
    size: number[];
};

export enum Modules {
    KPI	= 2,
    FocusWiki = 4,
    Game2048 = 7,
    Solitaire = 8,
    Calculator = 9,
    ActivityFeed = 10,
    IntervalPieChart = 12,
    Summary = 14,
    CallArrival = 15,
    Leaderboard = 16,
    AgentCounter = 19,
    CurrentAgentStates = 21,
    Challenges = 22,
    Gauge = 23,
    Notepad = 24,
    CurrentHoldTimes = 26,
    Interval = 27,
    Lift = 28,
    TopCallTimes = 29,
    YesGoDocsReport = 30,
    YesGoTitleReport = 31,
    Heatmap = 32,
    MassMutualRanking = 33,
    MassMutualIVRDetail	= 34,
    OutStates = 35,
    WorkAids = 37,
    Ets	= 39,
    EmailQueue = 40,
    MassMutualMultipleRanking = 41,
    CoachingSessions = 42,
    WebhelpRedialer	= 45,
    Announcements = 46,
    Games = 47,
    TopReasonsCount = 48,
    MetricReview = 54,
    RawCallData = 55,
    SatmetrixiFrame = 56,
    MassMutualIVR = 57,
    DataExport = 58,
    CurrentContacts = 59,
    AnomalyDetector = 60,
    SkillOverview = 61,
    SplitSkillReport = 62,
    WebsiteModule = 63,
    ServiceLevelDetail = 64,
    LoginLogout	= 65,
    SupervisorGames = 69,
    ReportingSet = 73,
    Tasks = 74,
    CurrentTickets = 76,
    MyRankings = 77,
    Surveys = 78,
    MassMutual = 83,
    FormScores = 84,
    IVR = 85,
    MetricsInterval = 111,
    MetricsSummary = 112,
    CXOneMetricBreakdown = 144,
    CXOneChallenges = 148,
    CXOneGames = 150,
    CXOneGamesTracker = 153,
    CXOneMyRanking = 155,
}

export interface CopyModule {
    copiedModule: DashboardModuleResponsiveInstance;
    copiedSettings: DashboardModuleEagerLoad;
}

export enum ModuleGroup {
    MetricVisualization = "Metric Visualization",
    CXoneACD = "CXone_ACD",
    CXoneQM = "CXone_QM",
    CXoneIA = "CXone_IA",
    CXoneCoaching = "CXone_Coaching",
    CXoneMetrics = "CXone_Metrics",
    CXonePM = "CXone_PM",
    RealTime = "Real-Time",
    Reports = "Reports",
    Gamification = "Gamification",
    Tools = "Tools",
    FreeTime = "Free Time",
    Custom = "Custom",
}
